import { Download, FileText } from 'lucide-react';
import { usePlatformChatMessageAttachmentDataQuery } from '../../../features/chat/queries/usePlatformChatMessageAttachmentDataQuery';
import { useBlobObjectUrl } from '../../../hooks/useBlobObjectUrl';
import {
  getAttachmentDisplayName,
  isAudioAttachment,
  type ChatMessageAttachment,
} from './chatMessageAttachmentUtils';
import './LiksChatMessageFileAttachment.css';

export type LiksChatMessageFileAttachmentProps = {
  sessionId: string;
  messageId: string;
  attachment: ChatMessageAttachment;
  downloadLabel: string;
  loadingLabel: string;
};

export function LiksChatMessageFileAttachment({
  sessionId,
  messageId,
  attachment,
  downloadLabel,
  loadingLabel,
}: LiksChatMessageFileAttachmentProps) {
  const attachmentDataQuery = usePlatformChatMessageAttachmentDataQuery(
    sessionId,
    messageId,
    attachment.id,
  );
  const objectUrl = useBlobObjectUrl(attachment.id, attachmentDataQuery.data);
  const displayName = getAttachmentDisplayName(attachment);
  const isAudio = isAudioAttachment(attachment);
  const handleDownload = () => {
    if (!objectUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = displayName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (attachmentDataQuery.isError) {
    return null;
  }

  if (attachmentDataQuery.isLoading || objectUrl == null) {
    return (
      <div
        className="liks-chat-message__attachment liks-chat-message__attachment--loading"
        aria-busy="true"
        aria-label={loadingLabel}
      >
        <div className="liks-chat-message__spinner" aria-hidden />
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="liks-chat-message__attachment liks-chat-message__attachment--audio">
        <audio
          className="liks-chat-message__attachment-audio"
          controls
          preload="metadata"
          src={objectUrl}
        >
          {displayName}
        </audio>
        <button
          type="button"
          className="liks-chat-message__attachment-download"
          aria-label={downloadLabel}
          onClick={handleDownload}
        >
          <Download
            className="liks-chat-message__attachment-download-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </div>
    );
  }

  return (
    <div className="liks-chat-message__attachment">
      <FileText
        className="liks-chat-message__attachment-icon"
        strokeWidth={2}
        aria-hidden
      />
      <span className="liks-chat-message__attachment-name">{displayName}</span>
      <button
        type="button"
        className="liks-chat-message__attachment-download"
        aria-label={downloadLabel}
        onClick={handleDownload}
      >
        <Download
          className="liks-chat-message__attachment-download-icon"
          strokeWidth={2}
          aria-hidden
        />
      </button>
    </div>
  );
}
